import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import { useTheme } from 'styled-components';
import { parseQuery, stringifyQuery } from '../../../../utils';
import Button from '../../../common/base/Button';
import Chip from '../../../common/base/Chip';
import Text from '../../../common/base/Text';
import DropdownMenu from '../../../common/DropdownMenu';
import EmptyState from '../../../common/EmptyState';
import { Div } from '../../../common/helpers/StyledUtils';
import Search from '../../../common/Search';
import Divider from '../../../common/base/Divider';
import TextSwitch from '../../../common/base/TextSwitch';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavHeader, StyledViewNavSecondary, StyledViewNavActions, ViewTitle } from '../../../layouts/ViewLayout';
import ConnectionsList from './List/ConnectionsList';
import { group_by_options, order_by_options } from './ResourcesContext';
import LINKS from '../../../../configs/links';
import useSWR from 'swr';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { DashboardContext } from '../DashboardContext';
const statuses = {
  active: 'Enabled',
  paused: 'Paused',
  disabled: 'Disabled'
};
interface WebhooksListFiltersProps {
  status?: keyof typeof statuses;
  source_id?: string;
  destination_id?: string;
}
interface ConnectionsContentProps {
  webhooks: any;
  search_term: string | undefined;
  setSearchTerm: ((term: string) => void) | undefined;
  setGroupBy: ((by: keyof typeof group_by_options) => void) | undefined;
  setOrderBy: ((by: keyof typeof order_by_options) => void) | undefined;
  order_by: keyof typeof order_by_options | undefined;
  group_by: keyof typeof group_by_options | undefined;
  resource_mutation_methods: any;
  updateSearchQuery: any;
  view: 'structured' | 'table';
  setView: (view: 'structured' | 'table') => void;
  has_connection: boolean;
  filters: WebhooksListFiltersProps;
  data: any;
}
const ConnectionsContent: React.FC<ConnectionsContentProps> = t0 => {
  const $ = _c(91);
  const {
    webhooks,
    search_term,
    setSearchTerm,
    setGroupBy,
    setOrderBy,
    order_by,
    group_by,
    updateSearchQuery,
    view,
    setView,
    has_connection,
    filters,
    data,
    resource_mutation_methods
  } = t0;
  const theme = useTheme();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    mutateHasConnection
  } = useContext(DashboardContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = APIMethodKeys.webhooks.count({});
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== HookdeckAPI.webhooks) {
    t2 = () => HookdeckAPI.webhooks.count({});
    $[1] = HookdeckAPI.webhooks;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: count_data
  } = useSWR(t1, t2);
  let t3;
  if ($[3] !== mutateHasConnection || $[4] !== resource_mutation_methods?.webhooks) {
    t3 = () => {
      resource_mutation_methods?.webhooks?.mutate();
      mutateHasConnection();
    };
    $[3] = mutateHasConnection;
    $[4] = resource_mutation_methods?.webhooks;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const mutate = t3;
  const t4 = !!filters.status;
  let t5;
  if ($[6] !== webhooks?.models) {
    t5 = webhooks?.models || [];
    $[6] = webhooks?.models;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = count_data?.count || 0;
  const t7 = !webhooks;
  const t8 = webhooks?.pagination;
  const t9 = webhooks?.pagination?.limit;
  let t10;
  if ($[8] !== t8 || $[9] !== t9) {
    t10 = {
      ...t8,
      page_size: t9
    };
    $[8] = t8;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== mutate || $[12] !== t10 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6 || $[16] !== t7 || $[17] !== view) {
    t11 = <ConnectionsList filtering={t4} view={view} connections={t5} total={t6} loading={t7} pagination={t10} mutate={mutate} />;
    $[11] = mutate;
    $[12] = t10;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = view;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const connections_list = t11;
  const default_order_by = view === "table" ? "full_name_asc" : "newest";
  const order_by_option = order_by_options[order_by || default_order_by] || order_by_options[default_order_by];
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      y: 5
    };
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      align: "center",
      justify: "space-between",
      gap: 2
    };
    t14 = <ViewTitle title="Connections" tooltip="Connections let you route events from a source to one or many destinations. Connections support rules, which dictate the behavior of events in transit, and pausing, which allows you to temporarily halt transient events." link={LINKS.product_docs.connections} link_label="Connections docs" />;
    $[20] = t13;
    $[21] = t14;
  } else {
    t13 = $[20];
    t14 = $[21];
  }
  let t15;
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [{
      label: "Structured",
      key: "structured"
    }, {
      label: "Table",
      key: "table"
    }];
    t16 = {
      y: -0.5
    };
    $[22] = t15;
    $[23] = t16;
  } else {
    t15 = $[22];
    t16 = $[23];
  }
  let t17;
  if ($[24] !== setView) {
    t17 = view_0 => setView(view_0 as 'structured' | 'table');
    $[24] = setView;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t17 || $[27] !== view) {
    t18 = <Div flex={t13}>{t14}<TextSwitch options={t15} m={t16} small={true} active={view} onSelect={t17} /></Div>;
    $[26] = t17;
    $[27] = view;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <StyledViewNavActions><Button.Permission to={_temp} primary={true} icon="add_circle">New connection</Button.Permission></StyledViewNavActions>;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t18) {
    t20 = <StyledViewNavHeader p={t12}>{t18}{t19}</StyledViewNavHeader>;
    $[30] = t18;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      b: 3
    };
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      align: "center",
      gap: 3
    };
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      px: 300
    };
    $[34] = t23;
  } else {
    t23 = $[34];
  }
  const t24 = search_term || "";
  let t25;
  if ($[35] !== setSearchTerm || $[36] !== t24) {
    t25 = <Div w={t23}><Search onChange={setSearchTerm} value={t24} small={true} placeholder="Filter by ID or name..." /></Div>;
    $[35] = setSearchTerm;
    $[36] = t24;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  let t26;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = {
      min: 200
    };
    $[38] = t26;
  } else {
    t26 = $[38];
  }
  const t27 = statuses[filters.status as keyof typeof statuses] || "Status";
  const t28 = !filters.status;
  let t29;
  if ($[39] !== updateSearchQuery) {
    t29 = () => updateSearchQuery({
      status: undefined
    });
    $[39] = updateSearchQuery;
    $[40] = t29;
  } else {
    t29 = $[40];
  }
  let t30;
  if ($[41] !== t28 || $[42] !== t29) {
    t30 = {
      label: "All",
      selected: t28,
      onClick: t29
    };
    $[41] = t28;
    $[42] = t29;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = Object.entries(statuses);
    $[44] = t31;
  } else {
    t31 = $[44];
  }
  let t32;
  if ($[45] !== filters.status || $[46] !== updateSearchQuery) {
    t32 = t31.map(t33 => {
      const [key, label] = t33;
      return {
        label,
        selected: filters.status === key,
        onClick: () => updateSearchQuery({
          status: [key]
        })
      };
    });
    $[45] = filters.status;
    $[46] = updateSearchQuery;
    $[47] = t32;
  } else {
    t32 = $[47];
  }
  let t33;
  if ($[48] !== t30 || $[49] !== t32) {
    t33 = [t30, ...t32];
    $[48] = t30;
    $[49] = t32;
    $[50] = t33;
  } else {
    t33 = $[50];
  }
  let t34;
  if ($[51] !== t27 || $[52] !== t33) {
    t34 = <DropdownMenu icon="filter_list" placement="bottom-start" outline={true} expand_icon="chevron_down" parent_width={t26} label={t27} options={t33} />;
    $[51] = t27;
    $[52] = t33;
    $[53] = t34;
  } else {
    t34 = $[53];
  }
  let t35;
  if ($[54] !== t25 || $[55] !== t34) {
    t35 = <Div flex={t22}>{t25}{t34}</Div>;
    $[54] = t25;
    $[55] = t34;
    $[56] = t35;
  } else {
    t35 = $[56];
  }
  let t36;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = {
      align: "center",
      gap: 3
    };
    $[57] = t36;
  } else {
    t36 = $[57];
  }
  let t37;
  if ($[58] !== group_by || $[59] !== setGroupBy || $[60] !== view) {
    t37 = view === "structured" && <DropdownMenu placement="bottom-end" outline={true} icon="sort" expand_icon="chevron_down" parent_width={{
      min: 200
    }} label_prefix="Group by" label={group_by_options[group_by].label} options={Object.entries(group_by_options).map(t38 => {
      const [key_0, t39] = t38;
      const {
        label: label_0
      } = t39;
      return {
        label: label_0,
        selected: group_by === key_0,
        onClick: () => setGroupBy?.(key_0 as keyof typeof group_by_options)
      };
    })} />;
    $[58] = group_by;
    $[59] = setGroupBy;
    $[60] = view;
    $[61] = t37;
  } else {
    t37 = $[61];
  }
  let t38;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = {
      min: 200
    };
    $[62] = t38;
  } else {
    t38 = $[62];
  }
  const t39 = order_by_option.label;
  let t40;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = Object.entries(order_by_options);
    $[63] = t40;
  } else {
    t40 = $[63];
  }
  let t41;
  if ($[64] !== order_by || $[65] !== setOrderBy) {
    t41 = t40.map(t42 => {
      const [key_1, t43] = t42;
      const {
        label: label_1
      } = t43;
      return {
        label: label_1,
        selected: order_by === key_1,
        onClick: () => setOrderBy?.(key_1 as keyof typeof order_by_options)
      };
    });
    $[64] = order_by;
    $[65] = setOrderBy;
    $[66] = t41;
  } else {
    t41 = $[66];
  }
  let t42;
  if ($[67] !== order_by_option.label || $[68] !== t41) {
    t42 = <DropdownMenu placement="bottom-end" parent_width={t38} outline={true} icon="sort" expand_icon="chevron_down" label_prefix="Order by" label={t39} options={t41} />;
    $[67] = order_by_option.label;
    $[68] = t41;
    $[69] = t42;
  } else {
    t42 = $[69];
  }
  let t43;
  if ($[70] !== t37 || $[71] !== t42) {
    t43 = <Div flex={t36}>{t37}{t42}</Div>;
    $[70] = t37;
    $[71] = t42;
    $[72] = t43;
  } else {
    t43 = $[72];
  }
  let t44;
  if ($[73] !== t35 || $[74] !== t43) {
    t44 = <StyledViewNavSecondary p={t21}>{t35}{t43}</StyledViewNavSecondary>;
    $[73] = t35;
    $[74] = t43;
    $[75] = t44;
  } else {
    t44 = $[75];
  }
  let t45;
  if ($[76] !== t20 || $[77] !== t44) {
    t45 = <StyledViewNav>{t20}{t44}</StyledViewNav>;
    $[76] = t20;
    $[77] = t44;
    $[78] = t45;
  } else {
    t45 = $[78];
  }
  let t46;
  if ($[79] !== connections_list || $[80] !== data?.name || $[81] !== filters.destination_id || $[82] !== filters.source_id || $[83] !== has_connection || $[84] !== theme || $[85] !== updateSearchQuery || $[86] !== view) {
    t46 = !has_connection ? <EmptyState title="Route events to your desired destinations" description="Connections let you route events from a source to one or many destinations. You can apply rules to connections and pause them, dictating the behavior of events in transit." asset={`connections-${theme.mode}.png`} asset_position="background" cta={{
      label: "Create first connection",
      icon: "connections",
      to: "/create-first-connection"
    }} /> : view === "structured" ? <Div h={100} min_w={{
      px: 500
    }} p={6}>{filters.source_id && <Div flex={{
        align: "center"
      }} m={{
        b: 6
      }}><Text m={{
          r: 2
        }} semi={true}>Filtering on</Text><Chip label="test" label_icon="source" mono={true} icon="close" onClick={() => updateSearchQuery({
          source_id: undefined
        })} /></Div>}{filters.destination_id && <Div flex={{
        align: "center"
      }} m={{
        b: 4
      }}><Text m={{
          r: 2
        }} semi={true}>Filtering on</Text><Chip label={data?.name || "Loading..."} mono={true} label_icon="destination" icon="close" onClick={() => updateSearchQuery({
          destination_id: undefined
        })} /></Div>}{connections_list}</Div> : <Div h={100}>{filters.source_id && <Div flex={{
        align: "center"
      }} m={{
        y: 4,
        x: 8
      }}><Text m={{
          r: 2
        }} semi={true}>Filtering on source</Text><Chip label={data?.name || "Loading..."} label_icon="source" mono={true} icon="close" onClick={() => updateSearchQuery({
          source_id: undefined
        })} /></Div>}{filters.destination_id && <Div flex={{
        align: "center"
      }} m={{
        b: 4
      }}><Text m={{
          r: 2
        }} semi={true}>Filtering on destination</Text><Chip label={data?.name || "Loading..."} mono={true} label_icon="destination" icon="close" onClick={() => updateSearchQuery({
          destination_id: undefined
        })} /></Div>}{(filters.source_id || filters.destination_id) && <Divider />}{connections_list}</Div>;
    $[79] = connections_list;
    $[80] = data?.name;
    $[81] = filters.destination_id;
    $[82] = filters.source_id;
    $[83] = has_connection;
    $[84] = theme;
    $[85] = updateSearchQuery;
    $[86] = view;
    $[87] = t46;
  } else {
    t46 = $[87];
  }
  let t47;
  if ($[88] !== t45 || $[89] !== t46) {
    t47 = <StyledViewLayout><StyledViewContent>{t45}{t46}</StyledViewContent></StyledViewLayout>;
    $[88] = t45;
    $[89] = t46;
    $[90] = t47;
  } else {
    t47 = $[90];
  }
  return t47;
};
export default ConnectionsContent;
function _temp(location) {
  return {
    ...location,
    pathname: "/connections/new",
    search: `?${stringifyQuery({
      ...parseQuery(location.search),
      default_source_id: undefined
    })}`,
    state: {
      scroll: false
    }
  };
}